import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  HistoryTab as HistoryTabUI,
  HistoryListingDetail,
  isPermitEvent,
  isTransactionEvent,
  isRentalEvent,
  isSaleEvent,
  type EventFilter,
  type HistoryTabTranslations,
  type HistoryEvent,
} from '../../../../../molecules/HistoryTab'
import { BuildingPermitDetail } from '../../../../BuildingPermitDetail'
import { ResizableDetailPanel } from '../../../../ResizableDetailPanel'
import { DetailPanelHeader, DetailPanelContent } from '../../../../DetailPanel'
import { AnalysisSectionHeader } from '../../../../../AnalysisSectionHeader'
import { Loading } from '../../../../../atoms/Loading'
import { Text } from '../../../../../atoms/Text'
import { Box } from '../../../../../atoms/Box'
import { useAnalysisMode } from '../../../contexts/AnalysisModeContext'
import { useAnalysisMapSectionData } from '../../../contexts/MapSectionDataContext'
import { useHistoryData } from './hooks/useHistoryData'

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  min-height: 500px;
`

const DetailWrapper = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 180px);
  overflow: hidden;
`

const ContentScroller = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  min-height: 400px;
`

const ErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  color: ${({ theme }) => theme.colors.danger};
  font-size: 14px;
`

export const HistoryTab: React.FC = () => {
  const { t, i18n } = useTranslation(['history', 'common'])
  const locale = i18n.language || 'fr'

  const formatDate = useCallback(
    (dateString: string | Date) => {
      const date = new Date(dateString)
      return date.toLocaleDateString(`${locale}-CH`, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    },
    [locale]
  )

  const { entity, entityType } = useAnalysisMode()
  const { setActiveSection, clearSectionData } = useAnalysisMapSectionData()
  const { events, loading, error } = useHistoryData(entity)

  // Set map section based on entity type
  useEffect(() => {
    if (entityType === 'building') {
      setActiveSection('history')
    } else {
      setActiveSection(null)
      clearSectionData()
    }
  }, [entityType, setActiveSection, clearSectionData])

  // Track which filters are active
  const [activeFilterTypes, setActiveFilterTypes] = useState<Set<string>>(
    new Set(['permit', 'transaction', 'rental', 'sale'])
  )

  // Track selected event for detail view
  const [selectedEvent, setSelectedEvent] = useState<HistoryEvent | null>(null)

  // Handle event click
  const handleEventClick = useCallback((event: HistoryEvent) => {
    setSelectedEvent(event)
  }, [])

  // Calculate filters with counts
  const filters: EventFilter[] = useMemo(() => {
    const counts = events.reduce(
      (acc, event) => {
        acc[event.type] = (acc[event.type] || 0) + 1
        return acc
      },
      {} as Record<string, number>
    )

    return [
      {
        type: 'permit',
        label: t('filters.buildingPermits'),
        icon: 'building-permit',
        count: counts.permit || 0,
        active: activeFilterTypes.has('permit'),
      },
      {
        type: 'transaction',
        label: t('filters.transactions'),
        icon: 'real-estate-transaction',
        count: counts.transaction || 0,
        active: activeFilterTypes.has('transaction'),
      },
      {
        type: 'rental',
        label: t('filters.rentalListings'),
        icon: 'listing-rental',
        count: counts.rental || 0,
        active: activeFilterTypes.has('rental'),
      },
      {
        type: 'sale',
        label: t('filters.saleListings'),
        icon: 'listing-purchase',
        count: counts.sale || 0,
        active: activeFilterTypes.has('sale'),
      },
    ]
  }, [events, t, activeFilterTypes])

  const toggleFilter = (type: string) => {
    setActiveFilterTypes((prev) => {
      if (prev.size === 1 && prev.has(type)) {
        return new Set(['permit', 'transaction', 'rental', 'sale'])
      }
      return new Set([type])
    })
  }

  const translations: HistoryTabTranslations = {
    loading: t('common.loading'),
    error: t('common.error'),
    noEvents: t('timeline.noEvents'),
    // adjustFilters: t('common.noResults'),
    eventDetails: t('eventDetails'),
    back: t('back'),
    propertyForRent: t('detailView.propertyForRent'),
    propertyForSale: t('detailView.propertyForSale'),
    addressNotSpecified: t('detailView.addressNotSpecified'),
    published: t('detailView.published'),
    statusLabel: t('detailView.statusLabel'),
    active: t('detailView.active'),
    inactive: t('detailView.inactive'),
    onlineFor: t('detailView.onlineFor'),
    days: t('detailView.days'),
    rooms: t('detailView.rooms'),
    livingArea: t('detailView.livingArea'),
    landArea: t('detailView.landArea'),
    bathrooms: t('detailView.bathrooms'),
    floor: t('detailView.floor'),
    built: t('detailView.built'),
    description: t('detailView.description'),
    propertyDetails: t('detailView.propertyDetails'),
    propertyType: t('detailView.propertyType'),
    propertyCategory: t('detailView.propertyCategory'),
    constructionYear: t('detailView.constructionYear'),
    renovationYear: t('detailView.renovationYear'),
    available: t('detailView.available'),
    immediately: t('detailView.immediately'),
    furnished: t('detailView.furnished'),
    petsAllowed: t('detailView.petsAllowed'),
    energyLabel: t('detailView.energyLabel'),
    heatingType: t('detailView.heatingType'),
    propertyAgents: t('detailView.propertyAgents'),
    featuresAndAmenities: t('detailView.featuresAndAmenities'),
    parking: t('detailView.parking'),
    balcony: t('detailView.balcony'),
    terrace: t('detailView.terrace'),
    garden: t('detailView.garden'),
    garage: t('detailView.garage'),
    elevator: t('detailView.elevator'),
    sections: {
      transactionDetails: t('sections.transactionDetails'),
      parties: t('sections.parties'),
      propertyInfo: t('sections.propertyInfo'),
      workDescription: t('sections.workDescription'),
      permitDetails: t('sections.permitDetails'),
      projectInfo: t('sections.projectInfo'),
      inquiryPeriod: t('sections.inquiryPeriod'),
      transactionInfo: t('transactionDetail.sections.transactionInfo'),
      participants: t('transactionDetail.sections.participants'),
      properties: t('transactionDetail.sections.properties'),
      summary: t('transactionDetail.sections.summary'),
    },
    fields: {
      date: t('fields.date'),
      reason: t('fields.reason'),
      ownershipType: t('ownershipType'),
      buildingClassification: t('fields.buildingClassification'),
      buyers: t('fields.buyers'),
      sellers: t('fields.sellers'),
      area: t('fields.area'),
      units: t('fields.units'),
      previousTransaction: t('fields.previousTransaction'),
      permitNumber: t('fields.permitNumber'),
      workType: t('fields.workType'),
      classification: t('fields.classification'),
      projectArea: t('fields.projectArea'),
      owner: t('fields.owner'),
      architect: t('fields.architect'),
      projectValue: t('fields.projectValue'),
      inquiryStart: t('fields.inquiryStart'),
      inquiryEnd: t('fields.inquiryEnd'),
      totalPrice: t('transactionDetail.fields.totalPrice'),
      transactionDate: t('transactionDetail.fields.transactionDate'),
      transactionId: t('transactionDetail.fields.transactionId'),
      officialId: t('transactionDetail.fields.officialId'),
      transactionType: t('transactionDetail.fields.transactionType'),
      publicationDate: t('transactionDetail.fields.publicationDate'),
      description: t('fields.description'),
      participant: t('transactionDetail.fields.participant'),
      role: t('transactionDetail.fields.role'),
      participantType: t('transactionDetail.fields.participantType'),
      share: t('transactionDetail.fields.share'),
      property: t('transactionDetail.fields.property'),
      city: t('transactionDetail.fields.city'),
      postalCode: t('transactionDetail.fields.postalCode'),
      landArea: t('fields.landArea'),
      totalSurface: t('transactionDetail.fields.totalSurface'),
      legalType: t('transactionDetail.fields.legalType'),
      propertyNumber: t('transactionDetail.fields.propertyNumber'),
      numberOfBuyers: t('transactionDetail.fields.numberOfBuyers'),
      numberOfSellers: t('transactionDetail.fields.numberOfSellers'),
      numberOfProperties: t('transactionDetail.fields.numberOfProperties'),
    },
    permit: {
      title: t('timeline.buildingPermit'),
      fields: {
        permitNumber: t('fields.permitNumber'),
        workType: t('fields.workType'),
        owner: t('fields.owner'),
        architect: t('fields.architect'),
        projectValue: t('fields.projectValue'),
        projectArea: t('fields.projectArea'),
        classification: t('fields.classification'),
      },
    },
    transaction: {
      title: t('timeline.transaction'),
      fields: {
        buyers: t('fields.buyers'),
        sellers: t('fields.sellers'),
        reason: t('fields.reason'),
        ownershipType: t('ownershipType'),
        buildingClassification: t('fields.buildingClassification'),
        area: t('fields.area'),
        units: t('fields.units'),
        previousTransaction: t('fields.previousTransaction'),
      },
    },
    rental: {
      title: t('timeline.rentalListing'),
      fields: {
        propertyType: t('fields.propertyType'),
        propertyCategory: t('fields.propertyCategory'),
        area: t('fields.area'),
        rooms: t('fields.rooms'),
        bathrooms: t('fields.bathrooms'),
        floor: t('fields.floor'),
        constructionYear: t('fields.constructionYear'),
        broker: t('fields.broker'),
        availableDate: t('fields.availableDate'),
        furnished: t('fields.furnished'),
        petsAllowed: t('fields.petsAllowed'),
        parking: t('fields.parking'),
        balcony: t('fields.balcony'),
        terrace: t('fields.terrace'),
        garden: t('fields.garden'),
        energyLabel: t('fields.energyLabel'),
      },
      status: {
        active: t('status.active'),
        inactive: t('status.inactive'),
      },
      footer: {
        online: t('timeline.online'),
        days: t('timeline.days'),
      },
    },
    sale: {
      title: t('timeline.saleListing'),
      fields: {
        propertyType: t('fields.propertyType'),
        propertyCategory: t('fields.propertyCategory'),
        area: t('fields.area'),
        landArea: t('fields.landArea'),
        rooms: t('fields.rooms'),
        bathrooms: t('fields.bathrooms'),
        floor: t('fields.floor'),
        constructionYear: t('fields.constructionYear'),
        renovationYear: t('fields.renovationYear'),
        broker: t('fields.broker'),
        garage: t('fields.garage'),
        parking: t('fields.parking'),
        balcony: t('fields.balcony'),
        terrace: t('fields.terrace'),
        garden: t('fields.garden'),
        elevator: t('fields.elevator'),
        energyLabel: t('fields.energyLabel'),
        heatingType: t('fields.heatingType'),
      },
      status: {
        active: t('status.active'),
        inactive: t('status.inactive'),
      },
      footer: {
        online: t('timeline.online'),
        days: t('timeline.days'),
      },
    },
  }

  // Render detail panel based on event type
  const renderDetailPanel = () => {
    if (!selectedEvent) return null

    if (isPermitEvent(selectedEvent)) {
      return (
        <BuildingPermitDetail
          isOpen={true}
          permit={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          contained={true}
        />
      )
    }

    if (isTransactionEvent(selectedEvent)) {
      return (
        <ResizableDetailPanel
          isOpen={true}
          onClose={() => setSelectedEvent(null)}
          storageKey="history_transaction_detail_panel_width"
          defaultWidth="700px"
          minWidth={400}
          maxWidthPercentage={0.8}
          contained={true}
        >
          <DetailPanelHeader onClose={() => setSelectedEvent(null)}>
            <Text variant="caption" color="secondary">
              {translations?.transaction?.title}
            </Text>
          </DetailPanelHeader>
          <DetailPanelContent>
            <Box padding="24px">
              <Text
                variant="h3"
                weight="semibold"
                style={{ marginBottom: '16px' }}
              >
                {selectedEvent.transaction_step_name ||
                  translations?.transaction?.title}
              </Text>
              {selectedEvent.transaction_total_price && (
                <Text
                  variant="h2"
                  color="primary"
                  style={{ marginBottom: '16px' }}
                >
                  {new Intl.NumberFormat('fr-CH', {
                    style: 'currency',
                    currency: 'CHF',
                    minimumFractionDigits: 0,
                  }).format(selectedEvent.transaction_total_price)}
                </Text>
              )}
              {selectedEvent.date && (
                <Text
                  variant="body1"
                  color="secondary"
                  style={{ marginBottom: '8px' }}
                >
                  {translations?.fields?.date}:{' '}
                  {new Date(selectedEvent.date).toLocaleDateString()}
                </Text>
              )}
              {selectedEvent.buyers_count !== undefined && (
                <Text
                  variant="body1"
                  color="secondary"
                  style={{ marginBottom: '8px' }}
                >
                  {translations?.fields?.buyers}: {selectedEvent.buyers_count}
                </Text>
              )}
              {selectedEvent.sellers_count !== undefined && (
                <Text
                  variant="body1"
                  color="secondary"
                  style={{ marginBottom: '8px' }}
                >
                  {translations?.fields?.sellers}: {selectedEvent.sellers_count}
                </Text>
              )}
              {selectedEvent.properties_count !== undefined && (
                <Text
                  variant="body1"
                  color="secondary"
                  style={{ marginBottom: '8px' }}
                >
                  {translations?.fields?.numberOfProperties}:{' '}
                  {selectedEvent.properties_count}
                </Text>
              )}
              {selectedEvent.transaction_step_text && (
                <Text variant="body1" style={{ marginTop: '16px' }}>
                  {selectedEvent.transaction_step_text}
                </Text>
              )}
            </Box>
          </DetailPanelContent>
        </ResizableDetailPanel>
      )
    }

    if (isRentalEvent(selectedEvent) || isSaleEvent(selectedEvent)) {
      const typeLabel =
        selectedEvent.type === 'rental'
          ? translations.rental?.title || 'Rental Listing'
          : translations.sale?.title || 'Sale Listing'

      return (
        <ResizableDetailPanel
          isOpen={true}
          onClose={() => setSelectedEvent(null)}
          storageKey="history_listing_detail_panel_width"
          defaultWidth="700px"
          minWidth={400}
          maxWidthPercentage={0.8}
          contained={true}
        >
          <DetailPanelHeader onClose={() => setSelectedEvent(null)}>
            <Text variant="caption" color="secondary">
              {typeLabel}
            </Text>
          </DetailPanelHeader>
          <DetailPanelContent>
            <Box padding="24px">
              <HistoryListingDetail
                event={selectedEvent}
                translations={translations}
              />
            </Box>
          </DetailPanelContent>
        </ResizableDetailPanel>
      )
    }

    return null
  }

  // Derive header based on entity type
  const getHeaderTitle = () => {
    if (entityType === 'building') return t('buildingHistory')
    return t('plotHistory')
  }

  const getHeaderSubtitle = () => {
    if (entityType === 'building') {
      const egid =
        entity?.buildingCharacteristics?.egid ||
        entity?.buildingCharacteristics?.building_id
      return t('buildingHistorySubtitle', { egid: egid || '' })
    }
    return t('plotHistorySubtitle', { egrid: entity?.egrid_nb || '' })
  }

  // Loading state
  if (!entity) {
    return (
      <Container>
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      </Container>
    )
  }

  // Error state
  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    )
  }

  return (
    <Container>
      <AnalysisSectionHeader
        title={getHeaderTitle()}
        subtitle={getHeaderSubtitle()}
      />
      <DetailWrapper>
        <ContentScroller>
          <HistoryTabUI
            events={events}
            loading={loading}
            error={error}
            filters={filters}
            onFilterToggle={toggleFilter}
            onEventClick={handleEventClick}
            selectedEvent={selectedEvent}
            onCloseDetail={() => setSelectedEvent(null)}
            disableInlineDetail={true}
            translations={translations}
            formatDate={formatDate}
          />
        </ContentScroller>
        {renderDetailPanel()}
      </DetailWrapper>
    </Container>
  )
}
